import { createFileRoute, Link } from "@tanstack/react-router";
import { jobs, companyName, formatAdvert, advertSections, fetchPublicJobs, fetchInviteJob, type Job } from "@/components/site/jobs";
import { TagRow } from "@/components/site/TagPill";
import { useEffect, useState } from "react";
import { trackInviteApi } from "@/components/site/admin";
import { JobDetailSkeleton } from "@/components/cl/Skeletons";
import { fetchSavedJobs, getCandidateToken, saveJob, unsaveJob } from "@/lib/candidate-api";
import JobDetailView, { AdvertBlock, SimilarCard } from "@/components/cl/views/JobDetailView";

export const Route = createFileRoute("/jobs/$jobId")({
  validateSearch: (search: Record<string, unknown>): { inv?: string } =>
    typeof search["inv"] === "string" && search["inv"] ? { inv: search["inv"] as string } : {},
  loader: ({ params }) => {
    const job = jobs.find((j) => j.id === params.jobId);
    return { job: job ?? null, jobId: params.jobId };
  },
  head: ({ loaderData }) => {
    if (!loaderData?.job) {
      return { meta: [{ title: "Role | CLOSDD" }, { name: "robots", content: "noindex" }] };
    }
    const t = `${loaderData.job.title} at ${companyName(loaderData.job)} | CLOSDD`;
    const d = (advertSections(loaderData.job.advert)?.find((s) => s.text)?.text ?? formatAdvert(loaderData.job.advert)).slice(0, 155);
    return {
      meta: [
        { title: t },
        { name: "description", content: d },
        { property: "og:title", content: t },
        { property: "og:description", content: d },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  component: JobDetail,
});


export function AdvertBody({ job, inv = "" }: { job: Job; inv?: string }) {
  return (
    <>
      <TagRow tags={job.tags} size="sm" className="mb-4" />

      <div className="rounded-lg border border-purple-strong/10 bg-purple-soft/40 p-4">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink/40">Pay</p>
            <p className="mt-1 text-[16px] font-semibold text-ink">{job.pay}</p>
          </div>
          <div className="sm:text-right">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink/40">Pay structure</p>
            <p className="mt-1 text-[13px] font-semibold text-purple-strong">{job.payStructure}</p>
          </div>
        </div>
      </div>

      <div className="mt-7">
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/50">The advert</p>
        <p className="mt-2 max-w-[760px] whitespace-pre-line text-[14px] leading-[1.55] text-ink/70">{formatAdvert(job.advert)}</p>
      </div>

      <div className="mt-7 border-t border-ink/10 pt-5">
        <Link
          to="/apply/$jobId"
          params={{ jobId: job.id }}
          search={{ invite: "", inv }}
          className="inline-flex rounded-lg bg-purple-strong px-6 py-2.5 text-[12px] font-semibold uppercase tracking-[0.1em] text-white transition hover:opacity-90"
        >
          {inv ? "Submit your CLOSDD profile" : "Apply with your pitch"}
        </Link>
        {inv ? (
          <p className="mt-2 text-[13px] text-ink/60">Apply for this role by submitting your CLOSDD profile.</p>
        ) : null}
      </div>
    </>
  );
}

function JobDetail() {
  const { job: staticJob, jobId } = Route.useLoaderData();
  const { inv = "" } = Route.useSearch();
  const [job, setJob] = useState<Job | null>(staticJob);
  const [loading, setLoading] = useState(!staticJob);
  const [others, setOthers] = useState<Job[]>([]);

  useEffect(() => {
    fetchPublicJobs()
      .then(setOthers)
      .catch(() => undefined);
  }, []);
  const similar = [...others, ...jobs].filter((j, i, a) => j.id !== jobId && a.findIndex((x) => x.id === j.id) === i).slice(0, 3);

  useEffect(() => {
    if (inv) void trackInviteApi(inv, "Viewed role");
  }, [inv]);

  // Save button: logged-in candidates only; anyone else is sent to log in first.
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!job || !getCandidateToken()) return;
    fetchSavedJobs()
      .then((list) => setSaved(list.some((s) => s.jobId === String(job.id))))
      .catch(() => undefined);
  }, [job]);
  const toggleSave = () => {
    if (!job) return;
    if (!getCandidateToken()) {
      window.location.href = "/login";
      return;
    }
    const next = !saved;
    setSaved(next);
    (next ? saveJob(job.id) : unsaveJob(job.id)).catch(() => setSaved(!next));
  };

  useEffect(() => {
    if (staticJob) return;
    // Roles are normally only visible here once Live AND approved by
    // CLOSDD — GET /api/public/jobs enforces that server-side. An invite
    // link is its own authorization to view this one specific job though,
    // so a candidate arriving with a valid ?inv= token can still see (and
    // apply for) a role CLOSDD hasn't approved for the public board yet.
    fetchPublicJobs()
      .then(async (live) => {
        const code = jobId.toLowerCase();
        const match = live.find((j) => j.id === jobId || j.ref?.toLowerCase() === code || j.legacyRef?.toLowerCase() === code);
        if (match) {
          setJob(match);
          return;
        }
        if (inv) {
          const invited = await fetchInviteJob(inv);
          if (invited) setJob(invited);
        }
      })
      .catch(() => {
        /* leaves job null — the "not available" screen covers this */
      })
      .finally(() => setLoading(false));
  }, [staticJob, jobId, inv]);

  if (loading) {
    return <JobDetailSkeleton />;
  }


  if (!job) {
    return (
      <div style={{ minHeight: "100vh", background: "#FFFFFF", color: "#0B2160", fontFamily: "Archivo, system-ui, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, letterSpacing: "-0.03em" }}>Role not available</h1>
          <p style={{ margin: "12px 0 0", fontSize: 18, color: "#3A3A40" }}>This advert may have been closed or removed.</p>
          <a href="/find-jobs" style={{ marginTop: 24, display: "inline-flex", alignItems: "center", minHeight: 52, padding: "0 28px", borderRadius: 4, background: "#FF5A3C", color: "#0B2160", fontWeight: 800, fontSize: 17, textDecoration: "none" }}>
            Browse all jobs
          </a>
        </div>
      </div>
    );
  }

  const company = companyName(job);
  const initialsOf = (name: string) =>
    name
      .split(/\s+/)
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  const posted = job.posted.replace(/^Posted /, "");
  const sections = advertSections(job.advert);
  const paras = formatAdvert(job.advert)
    .split(/\n{2,}/)
    .map((t) => t.trim())
    .filter(Boolean);
  const applyHref = `/apply/${job.id}?invite=${inv ? `&inv=${encodeURIComponent(inv)}` : ""}`;

  return (
    <JobDetailView
      v={{
        initials: initialsOf(company),
        title: job.title,
        titleUpper: job.title.toUpperCase(),
        company,
        location: job.location,
        postedFull: job.posted,
        posted,
        setting: job.setting,
        contract: job.contract,
        seniority: job.seniority,
        pay: job.pay,
        payStructure: job.payStructure,
        applyHref,
        saved,
        onSave: toggleSave,
        blocks: sections ? (
          <>
            {sections.map((s, i) => (
              <AdvertBlock key={i} label={s.label} text={s.text} items={s.items} />
            ))}
          </>
        ) : (
          <>
            <AdvertBlock label="The role" text={paras[0] ?? ""} />
            {paras.slice(1).map((t, i) => (
              <AdvertBlock key={i} label={i === 0 ? "More about the role" : "Role details"} text={t} />
            ))}
          </>
        ),
        similar: similar.map((j) => (
          <SimilarCard key={j.id} j={{ ...j, company: companyName(j), initials: initialsOf(companyName(j)) }} />
        )),
      }}
    />
  );
}
